/**
 * Is THIS order entitled to the subscription-only features?
 *
 * Two identities can own a purchase-flow order and they are judged differently:
 *
 *   - a client ordering for themselves → `UserDataContext`'s live
 *     `hasWorkspaceActiveSubscription`, which is already workspace-scoped;
 *   - an admin ordering on behalf → the entitlement of the CLIENT's selected
 *     workspace, captured by the admin gate into `targetWorkspaceIsSubscribed`.
 *
 * The split matters: the admin's own `hasWorkspaceActiveSubscription` describes
 * the admin's workspace, so reading it on an on-behalf order would hand a free
 * client paid features (or deny a paying one) purely on who is typing.
 *
 * A resumed on-behalf draft has no stored entitlement (the admin target is not
 * in the draft payload — DF-I27), so it resolves to `false`: withholding a paid
 * feature is recoverable by re-picking the client, whereas granting one is not.
 */

import { useUserData } from 'components/contexts/UserDataContext'

import { usePfStore } from '../../store'

/**
 * `true` when the order's owning workspace carries a paid entitlement.
 *
 * @example
 * const isSubscribed = usePfSubscriptionEntitlement()
 */
export const usePfSubscriptionEntitlement = (): boolean => {
  const { hasWorkspaceActiveSubscription } = useUserData()
  const targetUserId = usePfStore((s) => s.targetUserId)
  const targetWorkspaceIsSubscribed = usePfStore((s) => s.targetWorkspaceIsSubscribed)

  // An admin target set at all means the order belongs to somebody else, so the
  // logged-in user's own entitlement is the wrong answer whatever its value.
  if (targetUserId) return targetWorkspaceIsSubscribed === true

  return !!hasWorkspaceActiveSubscription
}

/**
 * Whether {@link usePfSubscriptionEntitlement}'s answer is known yet: false before the client's workspace has
 * resolved, and on an order placed on behalf whose client plan is not stored (a resumed draft, DF-I27), where
 * "not subscribed" is only the safe default. For a choice that must not be made on a guess.
 */
export const usePfSubscriptionEntitlementKnown = (): boolean => {
  const { isWorkspaceResolved } = useUserData()
  const targetUserId = usePfStore((s) => s.targetUserId)
  const targetWorkspaceIsSubscribed = usePfStore((s) => s.targetWorkspaceIsSubscribed)
  return targetUserId ? targetWorkspaceIsSubscribed !== null : isWorkspaceResolved
}
